// doctor-frontend/src/components/EncounterHeader.js
// Robust citizen + visit header for encounter pages.
// ADDITIVE improvements (no breaking changes):
// - Tenant-aware requests: adds /t/:slug prefix + X-Tenant headers + Authorization if present
// - Falls back across header → full visit → cached citizen gracefully
// - Leaves centre spelling EXACTLY as stored (e.g., "CRHA Dhundhara")
// - Keeps status/assignment chips if backend exposes them
// - IMPORTANT: Respects REACT_APP_API_BASE_URL (same pattern as http.js) without breaking relative mode
// - IMPORTANT FIX: If REACT_APP_API_BASE_URL is NOT set in dev, fallback to http://localhost:5000
//   so we don't accidentally call React dev server (3000) and get index.html instead of JSON.

import React, { useEffect, useState } from 'react';
import { Box, Typography, Chip, Stack, Divider, Avatar } from '@mui/material';

/* ------------------------ tiny utilities ------------------------ */
const initialsOf = (name = '') =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => (s[0] ? s[0].toUpperCase() : ''))
    .join('');

const safeStr = (v) => (v == null ? '' : String(v));

function tenantSlug() {
  try {
    const m = (window.location.pathname || '').match(/^\/t\/([^/]+)/);
    return m ? decodeURIComponent(m[1]) : '';
  } catch {
    return '';
  }
}

function tenantPrefix() {
  try {
    const m = (window.location.pathname || '').match(/^\/t\/[^/]+/);
    return m ? m[0] : '';
  } catch {
    return '';
  }
}

function pickToken() {
  const pull = (store) => {
    try {
      const keys = Object.keys(store || {});
      // avoid citizen tokens in CMFM encounter header
      const safeKeys = keys.filter((k) => !/citizen/i.test(k));
      // prefer cmfm/tenant tokens if present
      const prefer = safeKeys.find((k) => /cmfm|tenant|portal/i.test(k) && /token/i.test(k));
      if (prefer) return store.getItem(prefer);
      const any = safeKeys.find((k) => /token/i.test(k));
      return any ? store.getItem(any) : null;
    } catch {
      return null;
    }
  };
  return pull(localStorage) || pull(sessionStorage);
}

/**
 * IMPORTANT:
 * Some backends treat X-Tenant-Id as the actual Mongo tenantId (ObjectId).
 * Sending a slug in X-Tenant-Id can break tenant resolution.
 * So we only send X-Tenant-Id if we can find a real id in storage.
 */
function pickTenantId() {
  const pull = (store) => {
    try {
      const keys = Object.keys(store || {});
      // common keys used across the portal
      const candidates = ['tenantId', 'activeTenantId', 'cmfmTenantId', 'portalTenantId'];
      for (const k of candidates) {
        const v = store.getItem(k);
        if (v) return v;
      }

      // try any key that looks like tenant id
      const anyKey = keys.find((k) => /tenant/i.test(k) && /id/i.test(k));
      if (anyKey) {
        const v = store.getItem(anyKey);
        if (v) return v;
      }

      // try tenant object caches (if present)
      const objKey = keys.find((k) => /tenant/i.test(k) && /(obj|data|cache)/i.test(k));
      if (objKey) {
        const raw = store.getItem(objKey);
        if (raw) {
          try {
            const o = JSON.parse(raw);
            const id = o?._id || o?.id || o?.tenantId;
            if (id) return String(id);
          } catch {
            // ignore
          }
        }
      }
    } catch {
      // ignore
    }
    return null;
  };

  return pull(localStorage) || pull(sessionStorage);
}

/**
 * IMPORTANT:
 * If REACT_APP_API_BASE_URL is set (e.g., http://localhost:5000 or your Render backend URL),
 * we will call the API against that origin.
 * If not set, we keep original behavior BUT with a safe localhost fallback:
 * - If frontend is on localhost:3000, use http://localhost:5000 (same pattern as http.js)
 */
function computeApiBase() {
  const raw = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_BASE;
  if (raw) return String(raw).replace(/\/$/, '');

  try {
    const { hostname, port } = window.location;
    const isLocal = hostname === 'localhost' || hostname === '127.0.0.1';
    const isReactDev = String(port) === '3000';
    if (isLocal && isReactDev) return 'http://localhost:5000';
  } catch {
    // ignore
  }

  return ''; // preserve relative behavior for deployed builds
}

const API_BASE = computeApiBase();


function joinUrl(base, relPath) {
  const b = (base || '').replace(/\/+$/, ''); // trim trailing slashes only
  const r = (relPath || '').startsWith('/') ? relPath : `/${relPath}`;
  if (!b) return r;
  return `${b}${r}`;
}

async function apiGet(path) {
  const headers = { Accept: 'application/json' };

  const tok = pickToken();
  if (tok) headers.Authorization = `Bearer ${tok}`;

  const slug = tenantSlug();
  if (slug) {
    // Always safe: slug header
    headers['X-Tenant-Slug'] = slug;

    // Only send X-Tenant-Id if it's a real tenantId (not the slug)
    const tId = pickTenantId();
    if (tId) headers['X-Tenant-Id'] = tId;
  }

  // Build URL:
  // - If API_BASE is set, it is an ORIGIN (http://localhost:5000 or https://backend...)
  // - Always include tenantPrefix() when present so we hit /t/:slug/api/...
  const url = joinUrl(API_BASE, `${tenantPrefix()}${path}`);

  const r = await fetch(url, {
    headers,
    credentials: 'include', // safe even if you use bearer; helps if cookies ever used
  });

  if (!r.ok) return null;

  // If static site accidentally serves index.html, res.json() will throw.
  try {
    return await r.json();
  } catch {
    return null;
  }
}

function unwrapCitizen(payload) {
  if (!payload) return null;
  if (payload.citizen) return payload.citizen;
  if (payload.data && payload.data.citizen) return payload.data.citizen;
  if (payload.data && (payload.data.name || payload.data.citizenId)) return payload.data;
  if (payload.result && payload.result.citizen) return payload.result.citizen;
  if (payload.success && payload.success.citizen) return payload.success.citizen;
  if (payload.name || payload.citizenId) return payload; // sometimes top-level
  return null;
}

const pickStatus = (v) => safeStr(v?.prescription?.status) || safeStr(v?.status);

const pickAssignee = (v) => safeStr(v?.assignedToName || v?.assignedTo || v?.reviewerName);

const pickSubmitter = (v, citizen) =>
  safeStr(
    v?.createdByName ||
      v?.createdBy ||
      v?.submittedBy ||
      citizen?.submittedByName ||
      citizen?.submittedBy
  );

const pickLastActionBy = (v) =>
  safeStr(
    v?.lastActionByName ||
      v?.approvedByName ||
      v?.returnedByName ||
      v?.approvedBy ||
      v?.returnedBy
  );

function fmtDateISOish(d) {
  try {
    const dt = d ? new Date(d) : null;
    if (!dt || Number.isNaN(dt.getTime())) return '';
    return dt.toLocaleString(); // keep simple, respects locale
  } catch {
    return '';
  }
}

/* ------------------------ header loader ------------------------ */
async function loadHeader(visitId) {
  // 1) Try the light header endpoint, then full visit
  let v =
    (await apiGet(`/api/visits/${encodeURIComponent(visitId)}/header`)) ||
    (await apiGet(`/api/visits/${encodeURIComponent(visitId)}`)) ||
    null;

  // unwrap shapes like { success:true, visit:{...} }
  if (v && v.visit) v = { ...v.visit, ...v };

  // citizen id detection
  const citizenId =
    safeStr(v?.citizenId) ||
    localStorage.getItem('activeCitizenId') ||
    localStorage.getItem('citizenId') ||
    '';

  // 2) Fetch citizen if possible
  let citizen = null;
  if (citizenId) {
    const cRaw = await apiGet(`/api/citizens/${encodeURIComponent(citizenId)}`);
    citizen = unwrapCitizen(cRaw);
  }
  // 3) Fallback to cache
  if (!citizen) {
    try {
      citizen = JSON.parse(localStorage.getItem('activeCitizen') || 'null') || {};
    } catch {
      citizen = {};
    }
  }

  const name =
    safeStr(v?.citizenName) ||
    safeStr(citizen?.name) ||
    [safeStr(citizen?.firstName), safeStr(citizen?.lastName)].filter(Boolean).join(' ') ||
    safeStr(citizen?.fullName);

  const age = citizen?.age != null ? String(citizen.age) : v?.age != null ? String(v.age) : '';
  const sex = safeStr(citizen?.sex || citizen?.gender || v?.sex || v?.gender);
  const ageSex = [age, sex].filter(Boolean).join(' / ');

  const citizenReadableId =
    safeStr(v?.citizenReadableId) ||
    safeStr(citizen?.readableId || citizen?.citizenReadableId) ||
    citizenId;

  // IMPORTANT: do NOT “normalize” centre spelling; keep exactly as stored
  const centre =
    safeStr(v?.centre || v?.chc) ||
    safeStr(citizen?.centre) ||
    localStorage.getItem('activeCentre') ||
    localStorage.getItem('centre') ||
    '';

  const visitDate = v?.visitDate || v?.createdAt ? fmtDateISOish(v.visitDate || v.createdAt) : '';
  const avatarUrl = citizen?.avatarUrl || citizen?.photoUrl || '';

  // Optional meta
  const status = pickStatus(v);
  const assignedTo = pickAssignee(v);
  const submittedBy = pickSubmitter(v, citizen);
  const lastActionBy = pickLastActionBy(v);

  return {
    citizenName: name || '',
    ageSex,
    citizenReadableId,
    centre,
    visitDate,
    avatarUrl,
    status,
    assignedTo,
    submittedBy,
    lastActionBy,
  };
}

/* ------------------------ component ------------------------ */
export default function EncounterHeader({ visitId }) {
  const [hdr, setHdr] = useState(null);

  useEffect(() => {
    let alive = true;

    (async () => {
      if (!visitId) {
        // Not in an encounter yet; show citizen identity only from cache
        let citizen = {};
        try {
          citizen = JSON.parse(localStorage.getItem('activeCitizen') || 'null') || {};
        } catch {
          citizen = {};
        }
        if (!alive) return;
        setHdr({
          citizenName:
            citizen.name ||
            [safeStr(citizen.firstName), safeStr(citizen.lastName)].filter(Boolean).join(' ') ||
            safeStr(citizen.fullName) ||
            '',
          ageSex: [safeStr(citizen.age), safeStr(citizen.sex || citizen.gender)]
            .filter(Boolean)
            .join(' / '),
          citizenReadableId:
            safeStr(citizen.readableId || citizen.citizenReadableId) ||
            localStorage.getItem('activeCitizenId') ||
            '',
          centre: localStorage.getItem('activeCentre') || localStorage.getItem('centre') || '',
          visitDate: '',
          avatarUrl: citizen.avatarUrl || '',
          status: '',
          assignedTo: '',
          submittedBy: '',
          lastActionBy: '',
        });
        return;
      }

      const data = await loadHeader(visitId);
      if (alive) setHdr(data);
    })();

    return () => {
      alive = false;
    };
  }, [visitId]);

  const name = hdr?.citizenName || '—';
  const ageSex = hdr?.ageSex || '';
  const id = hdr?.citizenReadableId || '';
  const centre = hdr?.centre || '';
  const vDate = hdr?.visitDate || '';
  const avatarUrl = hdr?.avatarUrl || '';

  // Optional meta
  const status = hdr?.status || '';
  const assignedTo = hdr?.assignedTo || '';
  const submittedBy = hdr?.submittedBy || '';
  const lastActionBy = hdr?.lastActionBy || '';

  return (
    <Box sx={{ mb: 2 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          py: 1.25,
          gap: 1.5,
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
          <Avatar
            src={avatarUrl || undefined}
            alt={name}
            sx={{ width: 40, height: 40, bgcolor: 'primary.main', fontWeight: 700 }}
          >
            {!avatarUrl ? initialsOf(name || id) : null}
          </Avatar>
          <Stack spacing={0.5} sx={{ minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }} noWrap>
              {name}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0, flexWrap: 'wrap' }}>
              {ageSex ? <Chip label={ageSex} size="small" /> : null}
              {id ? <Chip label={id} size="small" /> : null}
              {centre ? <Chip label={centre} size="small" /> : null}
              {vDate ? <Chip label={vDate} size="small" /> : null}

              {/* approval / assignment chips when available */}
              {status ? (
                <Chip
                  label={`Status: ${status}`}
                  size="small"
                  color={
                    /approved/i.test(status)
                      ? 'success'
                      : /returned|rejected/i.test(status)
                      ? 'default'
                      : 'warning'
                  }
                />
              ) : null}
              {assignedTo ? <Chip label={`Assigned: ${assignedTo}`} size="small" /> : null}
              {submittedBy ? <Chip label={`Submitted: ${submittedBy}`} size="small" /> : null}
              {lastActionBy ? <Chip label={`Last action by: ${lastActionBy}`} size="small" /> : null}
            </Stack>
          </Stack>
        </Stack>
      </Box>
      <Divider />
    </Box>
  );
}
